/*!
 * AJBIR Interiors — "Atlas" theme behaviour
 * Vanilla JS, no dependencies. Every feature degrades to standard Shopify
 * behaviour (links and form posts) when JavaScript is unavailable.
 */
(() => {
  'use strict';

  const AJ = (window.AJBIR = window.AJBIR || {});
  const routes = AJ.routes || {};
  const strings = AJ.strings || {};
  const settings = AJ.settings || {};

  // Shopify HTML-escapes translations (e.g. d'envies → d&#39;envies); these strings are used as plain text.
  Object.keys(strings).forEach((key) => {
    const value = strings[key];
    if (typeof value !== 'string' || value.indexOf('&') === -1) return;
    const decoder = document.createElement('textarea');
    decoder.innerHTML = value;
    strings[key] = decoder.value;
  });

  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */
  const qs = (sel, el = document) => el.querySelector(sel);
  const qsa = (sel, el = document) => Array.from(el.querySelectorAll(sel));
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDesktop = () => window.matchMedia('(min-width: 1100px)').matches;
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const withExt = (url, ext) => (url || '').replace(/\/$/, '') + ext;
  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  const sectionHTML = (html, selector) => {
    const doc = parseHTML(html);
    const node = qs(selector, doc);
    return node ? node.innerHTML : null;
  };

  /* Toast -------------------------------------------------------------
   * Stays 6 s (9 s with an action) and pauses while it is hovered or focused, so a message can be read
   * and its action reached at any pace (WCAG 2.2.1). The action is a link ({ url, label }) or a button
   * ({ label, onClick }).
   * ------------------------------------------------------------------ */
  let toastTimer;
  let toastLeft = 0;
  let toastStart = 0;
  const toastHide = () => {
    const el = qs('.aj-toast');
    if (!el) return;
    clearTimeout(toastTimer);
    el.classList.remove('is-visible');
    // Once out of sight, drop the action so it cannot be reached with the keyboard
    setTimeout(() => {
      if (!el.classList.contains('is-visible')) el.textContent = '';
    }, 600);
  };
  const toastRun = (ms) => {
    clearTimeout(toastTimer);
    toastStart = Date.now();
    toastLeft = ms;
    toastTimer = setTimeout(toastHide, ms);
  };
  AJ.toast = (message, action) => {
    let el = qs('.aj-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'aj-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
      const pause = () => {
        clearTimeout(toastTimer);
        toastLeft = Math.max(0, toastLeft - (Date.now() - toastStart));
      };
      const resume = () => {
        if (!el.classList.contains('is-visible') || el.matches(':hover') || el.contains(document.activeElement)) return;
        toastRun(Math.max(toastLeft, 2000));
      };
      el.addEventListener('mouseenter', pause);
      el.addEventListener('focusin', pause);
      el.addEventListener('mouseleave', resume);
      el.addEventListener('focusout', () => setTimeout(resume, 0));
    }
    el.textContent = message;
    if (action && action.label && (action.url || action.onClick)) {
      const a = document.createElement(action.url ? 'a' : 'button');
      a.className = 'aj-toast__action';
      a.textContent = action.label;
      if (action.url) {
        a.href = action.url;
      } else {
        a.type = 'button';
        a.addEventListener('click', () => {
          toastHide();
          action.onClick();
        });
      }
      el.append(' ', a);
    }
    requestAnimationFrame(() => el.classList.add('is-visible'));
    toastRun(action ? 9000 : 6000);
  };

  /* Money ------------------------------------------------------------- */
  const formatMoney = (cents, format = AJ.moneyFormat || '{{amount}}') => {
    const value = Math.round(Number(cents) || 0);
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const [int, frac] = (n / 100).toFixed(decimals).split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? grouped + decimal + frac : grouped;
    };
    const match = String(format).match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return fmt(value, 2);
    let out;
    switch (match[1]) {
      case 'amount_no_decimals':
        out = fmt(value, 0);
        break;
      case 'amount_with_comma_separator':
        out = fmt(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = fmt(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        out = fmt(value, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        out = fmt(value, 0, ' ');
        break;
      case 'amount_with_space_separator':
        out = fmt(value, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        out = fmt(value, 2, ' ', '.');
        break;
      default:
        out = fmt(value, 2);
    }
    if (settings.hideZeros) out = out.replace(/[.,]00$/, '');
    if (settings.priceFormat === 'after') {
      // Same as snippets/aj-money: the number as Shopify writes it, the store's currency after it (1,290 Dhs);
      // a non-breaking space keeps the amount and the currency on one line
      const cur = String(format).replace(match[0], ' ').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
      return cur ? `${out}\u00a0${cur}` : out;
    }
    return String(format).replace(match[0], out);
  };
  AJ.formatMoney = formatMoney;
  // Same markup as snippets/aj-money with parts: true (currency and number in their own spans)
  const escHTML = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const moneyParts = (cents) => {
    const format = String(AJ.moneyFormat || '{{amount}}');
    const text = formatMoney(cents, format).replace(/<[^>]*>/g, '').trim();
    const placeholder = format.match(/\{\{\s*\w+\s*\}\}/);
    const num = formatMoney(cents, placeholder ? placeholder[0] : '{{amount}}');
    const at = num ? text.indexOf(num) : -1;
    if (at < 0) return escHTML(text);
    const before = text.slice(0, at).trim();
    const after = text.slice(at + num.length).trim();
    return `<span class="aj-money">${before ? `<span class="aj-money__cur">${escHTML(before)}</span> ` : ''}<span class="aj-money__num">${escHTML(num)}</span>${after ? ` <span class="aj-money__cur">${escHTML(after)}</span>` : ''}</span>`;
  };
  AJ.moneyParts = moneyParts;

  /* ------------------------------------------------------------------ *
   * Layers: drawers, search overlay — with focus trap
   * ------------------------------------------------------------------ */
  let activeLayer = null;
  let lastFocus = null;
  const lockScroll = (on) => document.documentElement.classList.toggle('aj-overflow-hidden', on);

  const Layers = {
    open(layer, opener) {
      if (!layer) return;
      let back = opener || document.activeElement;
      if (activeLayer && activeLayer !== layer) {
        // opened from inside the layer that closes now (e.g. the cart from the quick view): focus will go back
        // to what opened that layer, not to a button that is about to be hidden
        if (activeLayer.contains(back)) back = lastFocus;
        this.close(activeLayer, false);
      }
      lastFocus = back;
      layer.classList.add('is-open');
      layer.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      activeLayer = layer;
      if (layer.id) qsa(`[aria-controls="${layer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      const target = qs('[data-aj-search-input]', layer) || qs('.aj-drawer__panel, .aj-search__panel, .aj-qv__panel', layer);
      window.setTimeout(() => target && target.focus({ preventScroll: true }), 80);
      layer.dispatchEvent(new CustomEvent('aj:layer:open', { bubbles: true }));
    },
    close(layer, restoreFocus = true) {
      if (!layer || !layer.classList.contains('is-open')) return;
      layer.classList.remove('is-open');
      layer.setAttribute('aria-hidden', 'true');
      if (layer.id) qsa(`[aria-controls="${layer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (activeLayer === layer) activeLayer = null;
      if (!activeLayer) lockScroll(false);
      if (restoreFocus && lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
      layer.dispatchEvent(new CustomEvent('aj:layer:close', { bubbles: true }));
    },
    get active() {
      return activeLayer;
    },
  };
  AJ.Layers = Layers;

  const layerFor = (name) => (name === 'search' ? qs('#AjSearch') : qs(`[data-aj-drawer="${name}"]`));

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-aj-open]');
    if (opener) {
      const layer = layerFor(opener.getAttribute('data-aj-open'));
      if (layer) {
        event.preventDefault();
        Layers.open(layer, opener);
      }
      return;
    }
    const closer = event.target.closest('[data-aj-close]');
    if (closer) {
      const layer = closer.closest('.aj-drawer, .aj-search, .aj-qv');
      if (layer) {
        event.preventDefault();
        Layers.close(layer);
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!activeLayer) return;
    if (event.key === 'Escape') {
      Layers.close(activeLayer);
      return;
    }
    if (event.key !== 'Tab') return;
    const focusables = qsa(FOCUSABLE, activeLayer).filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && (document.activeElement === first || !activeLayer.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ------------------------------------------------------------------ *
   * Header: announcement rotation, sticky state, mega menus
   * ------------------------------------------------------------------ */
  function initAnnouncement(root = document) {
    qsa('[data-aj-announcement]', root).forEach((bar) => {
      if (bar.dataset.ajInit) return;
      bar.dataset.ajInit = '1';
      const messages = qsa('[data-aj-announcement-message]', bar);
      if (messages.length < 2) return;
      let index = 0;
      let paused = false;
      const speed = Math.max(3, parseInt(bar.dataset.speed || '5', 10)) * 1000;
      bar.addEventListener('mouseenter', () => (paused = true));
      bar.addEventListener('mouseleave', () => (paused = false));
      bar.addEventListener('focusin', () => (paused = true));
      bar.addEventListener('focusout', () => (paused = false));
      window.setInterval(() => {
        if (paused || document.hidden) return;
        messages[index].classList.remove('is-active');
        index = (index + 1) % messages.length;
        messages[index].classList.add('is-active');
      }, speed);
    });
  }

  function initStickyHeader() {
    const group = qs('[data-aj-header-group]');
    if (!group) return;
    const root = document.documentElement;
    const bar = qs('[data-aj-announcement]', group);
    const header = qs('[data-aj-header]', group);
    // Header heights feed CSS variables. Measuring them straight away would force the browser to lay out the
    // whole page while it is still loading, so sizes are read when the browser reports them (ResizeObserver)
    // or on the next frame.
    const setVar = (name, px) => root.style.setProperty(name, `${Math.round(px)}px`);
    // Desktop: the header shrinks once the page scrolls; its full height stays reserved (CSS min-height on the
    // sticky section) so the content below does not jump.
    let reserved = 0;
    const reserve = () => {
      if (group.dataset.sticky !== 'true' || !isDesktop() || group.classList.contains('is-compact')) return;
      const h = Math.round(group.getBoundingClientRect().height);
      if (h > reserved) {
        reserved = h;
        setVar('--aj-header-reserve', h);
      }
    };
    window.addEventListener(
      'resize',
      debounce(() => {
        if (group.classList.contains('is-compact')) return;
        reserved = 0;
        root.style.removeProperty('--aj-header-reserve');
        reserve();
      }, 200)
    );
    if ('ResizeObserver' in window) {
      if (!bar) setVar('--aj-announcement-h', 0);
      const ro = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const box = entry.borderBoxSize && entry.borderBoxSize[0];
          const h = box ? box.blockSize : entry.target.offsetHeight;
          setVar(entry.target === bar ? '--aj-announcement-h' : '--aj-header-h', h);
        });
        reserve();
      });
      if (header) ro.observe(header);
      if (bar) ro.observe(bar);
    } else {
      const measure = () => {
        setVar('--aj-announcement-h', bar ? bar.offsetHeight : 0);
        if (header) setVar('--aj-header-h', header.offsetHeight);
        reserve();
      };
      requestAnimationFrame(measure);
      window.addEventListener('resize', debounce(measure, 150));
    }
    let ticking = false;
    let scrolled = null;
    let compact = null;
    const update = () => {
      const y = window.scrollY;
      // Only touch the DOM when the state actually changes.
      if ((y > 8) !== scrolled) {
        scrolled = y > 8;
        group.classList.toggle('is-scrolled', scrolled);
      }
      if ((y > 140) !== compact) {
        compact = y > 140;
        group.classList.toggle('is-compact', compact);
      }
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    window.requestAnimationFrame(update);
  }

  function initMegaMenus(root = document) {
    // Pointer: menus open on hover (a tap on a touch screen opens, a second tap follows the link).
    // Keyboard: Tab moves from category to category; Enter or Space on the small button after a category
    // opens its menu (disclosure pattern), Arrow Down on the category opens it and moves into it, Escape closes.
    const setOpen = (el, isOpen) => {
      el.classList.toggle('is-open', isOpen);
      const toggle = qs('.aj-nav__toggle', el);
      if (toggle) toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    };
    qsa('[data-aj-mega-item]', root).forEach((item) => {
      if (item.dataset.ajInit) return;
      item.dataset.ajInit = '1';
      const link = qs('.aj-nav__link', item);
      const toggle = qs('.aj-nav__toggle', item);
      let openTimer;
      let closeTimer;
      let lastPointer = 'mouse';

      const open = () => {
        qsa('[data-aj-mega-item].is-open').forEach((other) => other !== item && setOpen(other, false));
        setOpen(item, true);
      };
      const close = () => setOpen(item, false);

      item.addEventListener('pointerenter', (e) => {
        lastPointer = e.pointerType;
        if (!isDesktop() || e.pointerType === 'touch') return;
        clearTimeout(closeTimer);
        openTimer = setTimeout(open, 70);
      });
      item.addEventListener('pointerleave', (e) => {
        if (!isDesktop() || e.pointerType === 'touch') return;
        clearTimeout(openTimer);
        closeTimer = setTimeout(close, 160);
      });
      link.addEventListener('pointerdown', (e) => (lastPointer = e.pointerType));
      link.addEventListener('click', (e) => {
        if (!isDesktop()) return;
        if (lastPointer === 'touch' && !item.classList.contains('is-open')) {
          e.preventDefault();
          open();
        }
      });
      link.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || ((e.key === ' ' || e.key === 'Enter') && e.altKey)) {
          e.preventDefault();
          open();
          const first = qs('.aj-mega a', item);
          if (first) first.focus();
        }
      });
      if (toggle) {
        toggle.addEventListener('click', () => {
          if (item.classList.contains('is-open')) close();
          else open();
        });
      }
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) close();
      });
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && item.classList.contains('is-open')) {
          close();
          (toggle || link).focus();
        }
      });
    });
    if (!initMegaMenus.bound) {
      initMegaMenus.bound = true;
      document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-aj-mega-item]')) qsa('[data-aj-mega-item].is-open').forEach((el) => setOpen(el, false));
      });
    }
  }

  /* ------------------------------------------------------------------ *
   * Predictive search
   * ------------------------------------------------------------------ */
  function initSearch() {
    const layer = qs('#AjSearch');
    if (!layer) return;
    const input = qs('[data-aj-search-input]', layer);
    const results = qs('[data-aj-search-results]', layer);
    // popular searches and the « Trouvez votre ambiance » link make way for the results
    const extras = qsa('[data-aj-search-popular], [data-aj-ambiance-link]', layer);
    const status = qs('[data-aj-search-status]', layer);
    if (!input || !results || !routes.predictiveSearch) return;
    let controller;

    const render = debounce(async () => {
      const q = input.value.trim();
      if (controller) controller.abort();
      if (q.length < 2) {
        results.innerHTML = '';
        if (status) status.textContent = '';
        extras.forEach((el) => (el.hidden = false));
        return;
      }
      controller = new AbortController();
      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,collection,page,article,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        section_id: 'aj-predictive-search',
      });
      try {
        const res = await fetch(`${routes.predictiveSearch}?${params.toString()}`, { signal: controller.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const inner = sectionHTML(html, '#shopify-section-aj-predictive-search') ?? html;
        results.innerHTML = inner;
        extras.forEach((el) => (el.hidden = true));
        const statusText = qs('[data-aj-search-status-text]', results);
        if (status) status.textContent = statusText ? statusText.textContent.trim() : '';
      } catch (err) {
        if (err.name !== 'AbortError') results.innerHTML = '';
      }
    }, 260);

    input.addEventListener('input', render);
    layer.addEventListener('aj:layer:close', () => {
      if (controller) controller.abort();
    });
  }

  /* ------------------------------------------------------------------ *
   * A babouche added to the cart: its photo turns round and a small embroidered pair walks once round it,
   * closing the circle (Theme settings › Ajbir — Babouches; drawn by assets/aj-babouches.js, loaded on demand).
   * ------------------------------------------------------------------ */
  const BabouchesLap = {
    handle: (text) => String(text || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-'),
    wants(item, data) {
      const mode = settings.bbCart || 'off';
      if (mode === 'off' || !item || !item.image) return false;
      if (mode === 'all') return true;
      const hay = this.handle([item.title, data && data.product_title, data && data.product_type, data && data.handle].join(' '));
      return (settings.bbWords || []).some((word) => word && hay.indexOf(word) > -1);
    },
    load() {
      if (AJ.Babouches) return Promise.resolve(AJ.Babouches);
      if (!this.loading) {
        const src = AJ.assets && AJ.assets.babouches;
        this.loading = !src
          ? Promise.resolve(null)
          : new Promise((resolve) => {
            const tag = document.createElement('script');
            tag.src = src;
            tag.onload = () => resolve(AJ.Babouches || null);
            tag.onerror = () => resolve(null);
            document.head.appendChild(tag);
          });
      }
      return this.loading;
    },
    // walk = true: round photo with the walking pair; false: the usual photo
    set(media, walk) {
      const old = qs('.aj-added__walk', media);
      if (old) {
        if (AJ.Babouches) AJ.Babouches.unmount(old);
        old.remove();
      }
      media.classList.toggle('aj-added__media--walk', walk);
      if (!walk) return;
      const colour = ['rouge', 'terracotta', 'sable'].indexOf(settings.bbColour) > -1 ? settings.bbColour : 'rouge';
      media.insertAdjacentHTML('beforeend', `<svg class="aj-added__walk" viewBox="0 0 100 100" aria-hidden="true" focusable="false" data-aj-bb="lap" data-aj-bb-colour="${colour}"><g data-prints></g><g data-shadows></g><g data-shoes></g></svg>`);
      const ring = qs('.aj-added__walk', media);
      this.load().then((B) => {
        if (B && ring.isConnected) B.lap(ring);
      });
    },
  };

  /* ------------------------------------------------------------------ *
   * "Added to cart" confirmation: a small card under the header, near the bag icon,
   * with the product, and a link that opens the cart. It goes away by itself.
   * ------------------------------------------------------------------ */
  const Added = {
    el: null,
    timer: null,
    build() {
      const el = document.createElement('div');
      el.className = 'aj-added';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      const opens = Cart.usesDrawer() ? ' data-aj-open="cart" aria-controls="AjCartDrawer"' : '';
      el.innerHTML =
        '<div class="aj-added__card">' +
        '<span class="aj-added__media"><img alt="" width="56" height="70"></span>' +
        '<div class="aj-added__body"><p class="aj-added__title"><svg class="aj-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M5 12.5l4.2 4.2L19 7"/></svg><span data-aj-added-title></span></p>' +
        '<p class="aj-added__product" data-aj-added-product></p>' +
        `<a class="aj-added__link" href="${routes.cart}"${opens} data-aj-added-link></a></div>` +
        '<button type="button" class="aj-added__close" data-aj-added-close><svg class="aj-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
        '</div>';
      qs('[data-aj-added-close]', el).setAttribute('aria-label', strings.close || 'Close');
      qs('[data-aj-added-link]', el).textContent = strings.viewCart || '';
      qs('[data-aj-added-close]', el).addEventListener('click', () => this.hide());
      qs('[data-aj-added-link]', el).addEventListener('click', () => this.hide());
      // stays while the pointer or the keyboard is on it
      ['mouseenter', 'focusin'].forEach((ev) => el.addEventListener(ev, () => clearTimeout(this.timer)));
      ['mouseleave', 'focusout'].forEach((ev) => el.addEventListener(ev, () => this.later(2500)));
      document.body.appendChild(el);
      this.el = el;
      return el;
    },
    later(ms) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.hide(), ms);
    },
    show(item, data) {
      const el = this.el || this.build();
      const added = strings.added || '';
      qs('[data-aj-added-title]', el).textContent = added.charAt(0).toUpperCase() + added.slice(1);
      qs('[data-aj-added-product]', el).textContent = [item.title, item.detail].filter(Boolean).join(' · ');
      const media = qs('.aj-added__media', el);
      const img = qs('img', media);
      if (item.image) img.src = item.image;
      media.hidden = !item.image;
      BabouchesLap.set(media, BabouchesLap.wants(item, data));
      // just under the header when it is on screen, otherwise at the top of the window
      const header = qs('[data-aj-header]');
      const bottom = header ? header.getBoundingClientRect().bottom : 0;
      el.style.top = `${Math.max(0, bottom) + 10}px`;
      el.classList.remove('is-visible');
      void el.offsetWidth;
      el.classList.add('is-visible');
      this.later(4200);
    },
    hide() {
      clearTimeout(this.timer);
      if (this.el) this.el.classList.remove('is-visible');
    },
  };
  AJ.Added = Added;

  /* ------------------------------------------------------------------ *
   * Cart (AJAX API + Section Rendering API)
   * ------------------------------------------------------------------ */
  const Cart = {
    sections() {
      const ids = ['aj-cart-drawer'];
      qsa('[data-aj-main-cart]').forEach((el) => el.dataset.section && ids.push(el.dataset.section));
      return ids;
    },
    async request(url, body, isForm = false) {
      const sections = this.sections();
      let options;
      if (isForm) {
        body.append('sections', sections.join(','));
        body.append('sections_url', window.location.pathname);
        options = { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body };
      } else {
        options = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...body, sections, sections_url: window.location.pathname }),
        };
      }
      const res = await fetch(url, options);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) {
        const error = new Error(data.description || data.message || strings.cartError || 'Error');
        error.data = data;
        throw error;
      }
      return data;
    },
    render(sectionsData) {
      if (!sectionsData) return;
      const drawerHTML = sectionsData['aj-cart-drawer'];
      if (drawerHTML) {
        const doc = parseHTML(drawerHTML);
        const fresh = qs('[data-aj-cart-inner]', doc);
        const current = qs('[data-aj-cart-inner]');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
        const countNode = qs('[data-aj-cart-count-source]', doc);
        if (countNode) this.setCount(parseInt(countNode.getAttribute('data-aj-cart-count-source'), 10) || 0);
      }
      qsa('[data-aj-main-cart]').forEach((el) => {
        const html = sectionsData[el.dataset.section];
        if (!html) return;
        const fresh = qs('[data-aj-main-cart]', parseHTML(html));
        if (fresh) el.innerHTML = fresh.innerHTML;
      });
      initReveal(document);
    },
    setCount(count) {
      qsa('[data-aj-cart-count]').forEach((el) => {
        const previous = el.textContent;
        el.textContent = count;
        el.dataset.count = count;
        if (String(count) !== previous) {
          el.classList.remove('is-bump');
          void el.offsetWidth;
          el.classList.add('is-bump');
        }
      });
    },
    async add(formData, item) {
      const data = await this.request(withExt(routes.cartAdd, '.js'), formData, true);
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('aj:cart:added', { detail: data }));
      this.afterAdd(data, item);
      return data;
    },
    async addItems(items, item) {
      const data = await this.request(withExt(routes.cartAdd, '.js'), { items });
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('aj:cart:added', { detail: data }));
      this.afterAdd(data, item);
      return data;
    },
    // The cart drawer exists unless the cart is a page; it opens from the bag icon.
    usesDrawer() {
      const drawer = qs('[data-aj-drawer="cart"]');
      return settings.cartType !== 'page' && drawer && !document.body.classList.contains('template-cart') ? drawer : null;
    },
    // Only with "Open the cart drawer" does adding a product open it (at once, filled in when the cart answers);
    // otherwise the shopper stays on the page and a short confirmation says the product is in the cart.
    opensOnAdd() {
      return settings.cartType === 'drawer' ? this.usesDrawer() : null;
    },
    pending(on, opener, item) {
      const drawer = this.usesDrawer();
      if (!drawer) return;
      drawer.classList.toggle('is-pending', on);
      const panel = qs('.aj-drawer__panel', drawer);
      if (panel) panel.setAttribute('aria-busy', on ? 'true' : 'false');
      const live = qs('[data-aj-cart-live]', drawer);
      if (live) live.textContent = on ? strings.adding || '' : '';
      qsa('[data-aj-cart-adding]', drawer).forEach((el) => el.remove());
      if (on) this.preview(drawer, item);
      if (on && !drawer.classList.contains('is-open')) Layers.open(drawer, opener || document.activeElement);
    },
    // What the shopper is adding, read from the page: shown in the drawer at once, replaced by the real line when the cart answers.
    previewFrom(form) {
      const item = { title: '', detail: '', image: '' };
      if (!form) return item;
      const card = form.closest('.aj-card');
      const productRoot = form.hasAttribute('data-aj-product-form') ? form.closest('[data-aj-product]') : null;
      const imageOf = (img) => (img ? img.currentSrc || img.src || '' : '');
      const named = qs('[data-aj-variant-title]', form); // card and mini buttons of products with options
      if (named) item.detail = named.dataset.ajVariantTitle || '';
      if (card) {
        const title = qs('.aj-card__title', card);
        item.title = title ? title.textContent.trim() : '';
        item.image = imageOf(qs('.aj-card__media img', card) || qs('img', card));
      } else if (productRoot) {
        const title = qs('.aj-product__title', productRoot);
        item.title = title ? title.textContent.trim() : '';
        let variant = null;
        try {
          const json = qs('[data-aj-product-json]', productRoot);
          const id = (qs('input[name="id"]', form) || {}).value;
          variant = json ? JSON.parse(json.textContent).variants.find((v) => String(v.id) === String(id)) : null;
        } catch (e) {
          variant = null;
        }
        if (variant && variant.options && !(variant.options.length === 1 && variant.options[0] === 'Default Title')) item.detail = variant.options.join(' / ');
        const slide = variant && variant.featured_media_id ? qs(`[data-media-id="${variant.featured_media_id}"] img`, productRoot) : null;
        item.image = imageOf(slide || qs('[data-aj-gallery-track] img', productRoot));
      } else {
        const box = form.closest('li, article, [class*="__item"]');
        const title = box && qs('a[href*="/products/"], h3, h4', box);
        item.title = title ? title.textContent.trim() : '';
        item.image = imageOf(box && qs('img', box));
      }
      const qty = parseInt((qs('input[name="quantity"]', form) || {}).value, 10);
      if (qty > 1) item.detail = item.detail ? `${item.detail} · × ${qty}` : `× ${qty}`;
      return item;
    },
    preview(drawer, item) {
      const body = qs('[data-aj-cart-inner] .aj-drawer__body', drawer);
      if (!body) return;
      const el = document.createElement('div');
      el.className = 'aj-cart-adding';
      el.setAttribute('data-aj-cart-adding', '');
      el.setAttribute('aria-hidden', 'true'); // the live region already says "adding"
      if (item && item.image) {
        const media = document.createElement('span');
        media.className = 'aj-cart-adding__media';
        const img = document.createElement('img');
        img.src = item.image;
        img.alt = '';
        media.appendChild(img);
        el.appendChild(media);
      } else {
        el.classList.add('aj-cart-adding--bare');
      }
      const info = document.createElement('span');
      info.className = 'aj-cart-adding__info';
      [['title', item && item.title], ['detail', item && item.detail], ['status', strings.adding || '']].forEach(([part, text]) => {
        if (!text) return;
        const span = document.createElement('span');
        span.className = `aj-cart-adding__${part}`;
        span.textContent = text;
        info.appendChild(span);
      });
      el.appendChild(info);
      body.prepend(el);
    },
    afterAdd(data, item) {
      const drawer = this.opensOnAdd();
      if (drawer) {
        this.pending(false);
        if (!drawer.classList.contains('is-open')) Layers.open(drawer, document.activeElement);
        const live = qs('[data-aj-cart-live]', drawer);
        if (live) live.textContent = strings.added || '';
      } else {
        // added from the quick view: back to the page, where the confirmation shows
        if (Layers.active && Layers.active.hasAttribute('data-aj-quickview')) Layers.close(Layers.active);
        const fallback = { title: (data && (data.product_title || data.title)) || '', detail: '', image: (data && data.image) || '' };
        Added.show(item && item.title ? item : fallback, data);
      }
    },
    async change(key, quantity) {
      const selector = window.CSS && CSS.escape ? `[data-key="${CSS.escape(key)}"]` : `[data-key="${String(key).replace(/"/g, '\\"')}"]`;
      const lineEls = qsa(selector);
      lineEls.forEach((el) => el.classList.add('is-loading'));
      const seq = (this.seq = (this.seq || 0) + 1);
      try {
        const data = await this.request(withExt(routes.cartChange, '.js'), { id: key, quantity });
        // Several quick changes: only the latest response is rendered (it reflects all of them)
        if (seq === this.seq) {
          this.render(data.sections);
          this.setCount(data.item_count);
        }
        document.dispatchEvent(new CustomEvent('aj:cart:updated', { detail: data }));
      } catch (err) {
        lineEls.forEach((el) => el.classList.remove('is-loading'));
        if (seq === this.seq) this.refresh();
        const box = qs('[data-aj-cart-error]');
        if (box) {
          box.textContent = err.message;
          box.hidden = false;
        } else {
          AJ.toast(err.message);
        }
      }
    },
    async refresh() {
      const ids = this.sections().join(',');
      try {
        const res = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(ids)}`);
        const data = await res.json();
        this.render(data);
      } catch (e) {
        /* ignore */
      }
    },
    updateNote: debounce((note) => {
      fetch(withExt(routes.cartUpdate, '.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      });
    }, 500),
  };
  AJ.Cart = Cart;

  // Quantity steppers & removal inside cart lines (drawer + cart page)
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-aj-qty-step]');
    if (step) {
      const lineEl = step.closest('[data-aj-line]');
      const wrap = step.closest('[data-aj-qty]');
      const input = wrap && qs('input', wrap);
      if (!input) return;
      event.preventDefault();
      const delta = parseInt(step.dataset.ajQtyStep, 10) || 0;
      const min = lineEl ? 0 : parseInt(input.min || '1', 10);
      const max = input.max ? parseInt(input.max, 10) : Infinity;
      const next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || 0) + delta));
      input.value = next;
      if (lineEl) Cart.change(lineEl.dataset.key, next);
      else input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const remove = event.target.closest('[data-aj-line-remove]');
    if (remove) {
      event.preventDefault();
      const lineEl = remove.closest('[data-aj-line]');
      if (lineEl) Cart.change(lineEl.dataset.key, 0);
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-aj-line-qty]');
    if (input) {
      const lineEl = input.closest('[data-aj-line]');
      if (lineEl) Cart.change(lineEl.dataset.key, Math.max(0, parseInt(input.value, 10) || 0));
      return;
    }
    const autosubmit = event.target.closest('[data-aj-autosubmit]');
    if (autosubmit && autosubmit.form) autosubmit.form.submit();
  });

  document.addEventListener('input', (event) => {
    const note = event.target.closest('[data-aj-cart-note]');
    if (note) Cart.updateNote(note.value);
  });

  // Add-to-cart forms: product page form, card quick add, mini quick forms and bundles
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-aj-product-form], form.aj-card__quick, form[data-aj-quick-form], form[data-aj-bundle]');
    if (!form) return;
    if (!window.fetch || !window.FormData) return;
    event.preventDefault();
    const button = qs('[type="submit"]', form);
    if (button && (button.disabled || button.classList.contains('is-loading'))) return;
    const errorBox = qs('[data-aj-form-error]', form) || (form.hasAttribute('data-aj-product-form') ? qs('[data-aj-form-error]', form.closest('[data-aj-product]') || form) : null);
    if (errorBox) errorBox.hidden = true;
    const productRoot = form.hasAttribute('data-aj-product-form') ? form.closest('[data-aj-product]') : null;
    const busy = [button, ...(productRoot ? qsa('[data-aj-sticky-button]', productRoot) : [])].filter(Boolean);
    busy.forEach((b) => {
      b.classList.add('is-loading');
      b.setAttribute('aria-busy', 'true');
    });
    let openedEarly = false;
    try {
      if (form.hasAttribute('data-aj-bundle')) {
        const items = Bundle.items(form);
        if (!items.length) throw new Error(strings.bundleEmpty || strings.cartError || 'Error');
        const item = { title: form.dataset.ajAddTitle || '', detail: '', image: '' };
        if (Cart.opensOnAdd()) {
          openedEarly = true;
          Cart.pending(true, button, item);
        }
        await Cart.addItems(items, item);
      } else {
        const formData = new FormData(form);
        const item = Cart.previewFrom(form);
        if (Cart.opensOnAdd()) {
          openedEarly = true;
          Cart.pending(true, button, item);
        }
        await Cart.add(formData, item);
      }
      // the button itself confirms for a moment: a tick on the card buttons, "Added" on the product page
      (form.hasAttribute('data-aj-product-form') ? busy : [button].filter(Boolean)).forEach((b) => {
        b.classList.add('is-added');
        const label = qs('[data-aj-atc-label]', b);
        const before = label ? label.textContent : null;
        if (label && strings.addedShort) label.textContent = strings.addedShort;
        setTimeout(() => {
          b.classList.remove('is-added');
          if (label && label.textContent === strings.addedShort) label.textContent = before;
        }, 1800);
      });
    } catch (err) {
      if (openedEarly) {
        // Nothing was added: close the drawer again and explain next to the button.
        Cart.pending(false);
        const drawer = Cart.usesDrawer();
        if (drawer) Layers.close(drawer);
      }
      if (errorBox) {
        errorBox.textContent = err.message;
        errorBox.hidden = false;
        // e.g. submitted from the sticky bar while the form is off screen, or from a quick view that has closed
        const r = errorBox.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight || errorBox.closest('.aj-qv:not(.is-open)')) AJ.toast(err.message);
      } else {
        AJ.toast(err.message);
      }
    } finally {
      busy.forEach((b) => {
        b.classList.remove('is-loading');
        b.removeAttribute('aria-busy');
      });
    }
  });

  /* Bundles: several products added in one request ------------------- */
  const Bundle = {
    rows(form) {
      return qsa('[data-aj-bundle-item]', form);
    },
    included(row) {
      const check = qs('[data-aj-bundle-check]', row);
      if (check && !check.checked) return false;
      return !!qs('[data-aj-bundle-variant]', row);
    },
    variant(row) {
      const el = qs('[data-aj-bundle-variant]', row);
      if (!el) return null;
      const source = el.tagName === 'SELECT' ? el.options[el.selectedIndex] : el;
      if (!source) return null;
      return { id: parseInt(source.value, 10), price: parseInt(source.dataset.price || '0', 10) };
    },
    quantity(row) {
      const input = qs('[data-aj-bundle-qty]', row);
      return input ? Math.max(0, parseInt(input.value, 10) || 0) : 1;
    },
    items(form) {
      return this.rows(form)
        .filter((row) => this.included(row))
        .map((row) => {
          const v = this.variant(row);
          return v ? { id: v.id, quantity: this.quantity(row) } : null;
        })
        .filter((item) => item && item.id && item.quantity > 0);
    },
    update(form) {
      if (!form) return;
      let total = 0;
      this.rows(form).forEach((row) => {
        const included = this.included(row);
        const v = this.variant(row);
        row.classList.toggle('is-excluded', !included);
        const price = qs('[data-aj-bundle-price]', row);
        if (v && price) price.textContent = formatMoney(v.price);
        if (included && v) total += v.price * this.quantity(row);
      });
      const totalEl = qs('[data-aj-bundle-total]', form);
      if (totalEl) totalEl.textContent = formatMoney(total);
      const submit = qs('[data-aj-bundle-submit]', form);
      if (submit) submit.disabled = this.items(form).length === 0;
    },
  };
  AJ.Bundle = Bundle;
  document.addEventListener('change', (event) => {
    const form = event.target.closest('form[data-aj-bundle]');
    if (form) Bundle.update(form);
  });
  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-aj-bundle-qty]')) Bundle.update(event.target.closest('form[data-aj-bundle]'));
  });

  // Legacy (jQuery) add-to-cart in older sections: keep the new drawer in sync.
  const bindLegacyCart = () => {
    if (!window.jQuery || bindLegacyCart.done) return;
    bindLegacyCart.done = true;
    window.jQuery(document).ajaxComplete((e, xhr, opts) => {
      if (opts && opts.url && /\/cart\/(add|change|update|clear)/.test(opts.url) && !/sections=/.test(opts.url)) Cart.refresh();
    });
  };

  /* ------------------------------------------------------------------ *
   * Custom storefront events, readable by pixels (Settings › Customer events)
   * ------------------------------------------------------------------ */
  const track = (name, data = {}) => {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, data);
      }
    } catch (e) {
      /* analytics unavailable */
    }
  };
  AJ.track = track;

  /* ------------------------------------------------------------------ *
   * Wishlist: product handles in localStorage "wishlist" ("+handle+handle"), the format the previous
   * theme used, so favourites saved before the switch keep working.
   * ------------------------------------------------------------------ */
  const Wishlist = {
    key: 'wishlist',
    read() {
      try {
        return [...new Set((localStorage.getItem(this.key) || '').split('+').map((h) => h.trim()).filter(Boolean))];
      } catch (e) {
        return [];
      }
    },
    write(handles) {
      try {
        if (handles.length) localStorage.setItem(this.key, '+' + handles.join('+'));
        else localStorage.removeItem(this.key);
      } catch (e) {
        /* storage unavailable */
      }
    },
    sync(root = document) {
      const handles = this.read();
      qsa('[data-aj-wish]', root).forEach((btn) => {
        const active = handles.includes(btn.dataset.ajWish);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        btn.setAttribute('aria-label', active ? btn.dataset.labelRemove : btn.dataset.labelAdd);
      });
      qsa('[data-aj-wish-count]').forEach((el) => {
        el.textContent = handles.length;
        el.dataset.count = handles.length;
      });
    },
    // `at`: where to put a product back (undo keeps the list in the order it had)
    toggle(handle, at) {
      const handles = this.read();
      const i = handles.indexOf(handle);
      if (i > -1) handles.splice(i, 1);
      else if (typeof at === 'number' && at >= 0) handles.splice(Math.min(at, handles.length), 0, handle);
      else handles.push(handle);
      this.write(handles);
      this.sync();
      const added = i === -1;
      track(added ? 'ajbir_wishlist_add' : 'ajbir_wishlist_remove', { product_handle: handle });
      document.dispatchEvent(new CustomEvent('aj:wishlist', { detail: { handle, added, index: i } }));
      return added;
    },
  };
  AJ.Wishlist = Wishlist;
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-aj-wish]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    const added = Wishlist.toggle(btn.dataset.ajWish);
    btn.classList.remove('is-pop');
    void btn.offsetWidth;
    btn.classList.add('is-pop');
    // On the wishlist page the page itself says what happened (see initWishlistPage)
    if (added && !btn.closest('[data-aj-wishlist]')) {
      AJ.toast(strings.wishlistAdded || '', settings.wishlistPage ? { url: settings.wishlistPage, label: strings.wishlistView || '' } : null);
    }
  });

  /* Wishlist page: the saved products, drawn with the same card as everywhere else (section "aj-card") */
  function initWishlistPage(root = document) {
    qsa('[data-aj-wishlist]', root).forEach(async (el) => {
      if (el.dataset.ajInit) return;
      el.dataset.ajInit = '1';
      const list = qs('[data-aj-wishlist-list]', el);
      const empty = qs('[data-aj-wishlist-empty]', el);
      const count = qs('[data-aj-wishlist-count]', el);
      const base = routes.root.replace(/\/$/, '');
      const cols = parseInt(list.style.getPropertyValue('--cols'), 10) || 4;
      const update = () => {
        const n = qsa('[data-aj-wishlist-item]', list).length;
        list.hidden = n === 0;
        empty.hidden = n > 0;
        // A short list sits in the middle of the row on large screens, as in product grids
        list.classList.toggle('aj-row-partial', n > 0 && n < cols);
        list.style.setProperty('--cols-fill', Math.max(1, Math.min(n, cols)));
        if (count) {
          count.hidden = n === 0;
          count.textContent = (n === 1 ? el.dataset.countOne : el.dataset.countOther || '').replace('[count]', n);
        }
      };
      const card = async (handle) => {
        try {
          const res = await fetch(`${base}/products/${encodeURIComponent(handle)}?section_id=aj-card`);
          if (res.status === 404) return { handle, gone: true };
          if (!res.ok) return { handle };
          const html = sectionHTML(await res.text(), '[data-aj-card-root]');
          return { handle, html: html && html.trim() ? html : null };
        } catch (e) {
          return { handle };
        }
      };
      const handles = Wishlist.read();
      if (!handles.length) {
        list.innerHTML = '';
        update();
        return;
      }
      list.setAttribute('aria-busy', 'true');
      const cards = await Promise.all(handles.slice(0, 60).map(card));
      // Products deleted from the store leave the list; a network error keeps them for next time
      const gone = cards.filter((c) => c.gone).map((c) => c.handle);
      if (gone.length) Wishlist.write(Wishlist.read().filter((h) => !gone.includes(h)));
      list.innerHTML = '';
      cards.forEach((c) => {
        if (!c.html) return;
        const li = document.createElement('li');
        li.dataset.ajWishlistItem = c.handle;
        li.innerHTML = c.html;
        list.appendChild(li);
      });
      list.removeAttribute('aria-busy');
      Wishlist.sync();
      initReveal(list);
      update();

      document.addEventListener('aj:wishlist', (e) => {
        const { handle, added, index } = e.detail;
        const li = qsa('[data-aj-wishlist-item]', list).find((x) => x.dataset.ajWishlistItem === handle);
        if (added || !li) return;
        const next = li.nextElementSibling;
        const prev = li.previousElementSibling;
        const restoreFocus = li.contains(document.activeElement);
        li.remove();
        update();
        if (restoreFocus) {
          const target = qs('[data-aj-wish]', next || prev || list) || qs('a, button', empty);
          if (target) target.focus();
        }
        AJ.toast(strings.wishlistRemoved || '', {
          label: strings.undo || '',
          onClick: () => {
            if (!Wishlist.read().includes(handle)) Wishlist.toggle(handle, index);
            list.insertBefore(li, next && next.parentNode === list ? next : null);
            Wishlist.sync(li);
            update();
            const heart = qs('[data-aj-wish]', li);
            if (heart) heart.focus();
          },
        });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Reveal on scroll, parallax, sliders
   * ------------------------------------------------------------------ */
  let revealObserver;
  function initReveal(root = document) {
    const els = qsa('[data-aj-reveal]:not(.is-inview), .aj-reveal-img:not(.is-inview)', root);
    if (!document.documentElement.classList.contains('aj-motion') || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-inview'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-inview');
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -6% 0px', threshold: 0.08 }
      );
    }
    els.forEach((el) => revealObserver.observe(el));
  }

  const parallaxItems = new Set();
  let parallaxTicking = false;
  function updateParallax() {
    const vh = window.innerHeight;
    parallaxItems.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) return;
      const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
      const range = parseFloat(el.dataset.ajParallax || '6');
      el.style.setProperty('--aj-parallax-y', `${(-progress * range).toFixed(2)}%`);
    });
    parallaxTicking = false;
  }
  function initParallax(root = document) {
    if (!settings.parallax || reducedMotion() || !window.matchMedia('(min-width: 750px)').matches) return;
    qsa('[data-aj-parallax]', root).forEach((el) => {
      el.style.setProperty('--aj-parallax-scale', '1.12');
      parallaxItems.add(el);
    });
    if (!initParallax.bound) {
      initParallax.bound = true;
      window.addEventListener(
        'scroll',
        () => {
          if (!parallaxTicking) {
            parallaxTicking = true;
            requestAnimationFrame(updateParallax);
          }
        },
        { passive: true }
      );
    }
    updateParallax();
  }

  function initSliders(root = document) {
    qsa('[data-aj-slider]', root).forEach((slider) => {
      if (slider.dataset.ajInit) return;
      slider.dataset.ajInit = '1';
      const track = qs('.aj-slider__track', slider);
      const prev = qs('[data-aj-slider-prev]', slider);
      const next = qs('[data-aj-slider-next]', slider);
      if (!track) return;
      const nav = qs('.aj-slider__nav', slider);
      const update = () => {
        if (prev) prev.disabled = track.scrollLeft <= 4;
        if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
        if (nav) nav.style.visibility = prev && next && prev.disabled && next.disabled ? 'hidden' : '';
      };
      const by = (dir) => track.scrollBy({ left: dir * track.clientWidth * 0.85, behavior: reducedMotion() ? 'auto' : 'smooth' });
      if (prev) prev.addEventListener('click', () => by(-1));
      if (next) next.addEventListener('click', () => by(1));
      track.addEventListener('scroll', debounce(update, 60), { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      slider.ajUpdate = update;
      // Reading scroll sizes forces a layout; wait for the browser's own layout instead of forcing one during boot.
      requestAnimationFrame(update);
    });
  }

  /* Banner slideshow: dots, arrows, autoplay (paused on hover/focus/hidden tab) */
  function initSlideshows(root = document) {
    qsa('[data-aj-slideshow]', root).forEach((show) => {
      if (show.dataset.ajInit) return;
      show.dataset.ajInit = '1';
      const track = qs('[data-aj-slideshow-track]', show);
      const slides = qsa('[data-aj-slideshow-slide]', show);
      const dots = qsa('[data-aj-slideshow-dot]', show);
      const pauseBtn = qs('[data-aj-slideshow-pause]', show);
      if (!track || slides.length < 2) return;
      let index = 0;
      let timer = null;
      let hovered = false;
      let userPaused = false;
      const delay = (parseInt(show.dataset.autoplay, 10) || 0) * 1000;

      const setActive = (i) => {
        index = i;
        dots.forEach((dot, n) => {
          dot.classList.toggle('is-active', n === i);
          if (n === i) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      };
      const goTo = (i) => {
        const n = (i + slides.length) % slides.length;
        track.scrollTo({ left: slides[n].offsetLeft - track.offsetLeft, behavior: reducedMotion() ? 'auto' : 'smooth' });
        setActive(n);
      };
      const stop = () => {
        clearInterval(timer);
        timer = null;
      };
      const start = () => {
        stop();
        if (!delay || reducedMotion() || userPaused || hovered || (window.Shopify && window.Shopify.designMode)) return;
        timer = setInterval(() => {
          if (!document.hidden) goTo(index + 1);
        }, delay);
      };

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => entries.forEach((entry) => entry.isIntersecting && entry.intersectionRatio > 0.55 && setActive(slides.indexOf(entry.target))),
          { root: track, threshold: [0.6] }
        );
        slides.forEach((slide) => io.observe(slide));
      }
      dots.forEach((dot, i) => dot.addEventListener('click', () => { goTo(i); start(); }));
      const prev = qs('[data-aj-slideshow-prev]', show);
      const next = qs('[data-aj-slideshow-next]', show);
      if (prev) prev.addEventListener('click', () => { goTo(index - 1); start(); });
      if (next) next.addEventListener('click', () => { goTo(index + 1); start(); });
      show.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { hovered = true; stop(); } });
      show.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { hovered = false; start(); } });
      show.addEventListener('focusin', stop);
      show.addEventListener('focusout', (e) => { if (!show.contains(e.relatedTarget)) start(); });
      track.addEventListener('touchstart', stop, { passive: true });
      track.addEventListener('touchend', () => setTimeout(start, 4000), { passive: true });
      if (pauseBtn) {
        if (reducedMotion()) pauseBtn.hidden = true;
        pauseBtn.addEventListener('click', () => {
          userPaused = !userPaused;
          pauseBtn.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
          pauseBtn.setAttribute('aria-label', userPaused ? pauseBtn.dataset.labelPlay : pauseBtn.dataset.labelPause);
          if (userPaused) stop();
          else start();
        });
      }
      start();
    });
  }

  /* ------------------------------------------------------------------ *
   * Hero video (pause / play)
   * ------------------------------------------------------------------ */
  function initVideos(root = document) {
    qsa('[data-aj-video-toggle]', root).forEach((btn) => {
      if (btn.dataset.ajInit) return;
      btn.dataset.ajInit = '1';
      const video = qs('video', btn.closest('[data-aj-video-wrap]'));
      if (!video) return;
      if (reducedMotion()) {
        video.pause();
        video.removeAttribute('autoplay');
      }
      const sync = () => {
        btn.setAttribute('aria-pressed', video.paused ? 'true' : 'false');
        btn.innerHTML = video.paused ? btn.dataset.iconPlay : btn.dataset.iconPause;
      };
      btn.addEventListener('click', () => {
        if (video.paused) video.play();
        else video.pause();
        sync();
      });
      sync();
    });
  }

  /* ------------------------------------------------------------------ *
   * Footer accordions (collapsed on mobile)
   * ------------------------------------------------------------------ */
  function initFooter(root = document) {
    const mobile = !window.matchMedia('(min-width: 750px)').matches;
    qsa('[data-aj-footer-accordion]', root).forEach((details) => {
      if (mobile) details.removeAttribute('open');
      const summary = qs('summary', details);
      if (summary && !summary.dataset.ajInit) {
        summary.dataset.ajInit = '1';
        summary.addEventListener('click', (e) => {
          if (window.matchMedia('(min-width: 750px)').matches) e.preventDefault();
        });
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Product page
   * ------------------------------------------------------------------ */
  class Product {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.section;
      this.url = root.dataset.url;
      const json = qs('[data-aj-product-json]', root);
      this.data = json ? JSON.parse(json.textContent) : null;
      this.form = qs('form[data-aj-product-form]', root);
      this.idInput = this.form ? qs('input[name="id"]', this.form) : null;
      this.atc = qs('[data-aj-atc]', root);
      this.sticky = qs('[data-aj-sticky-atc]', root);
      this.initOptions();
      this.initGallery();
      this.initLightbox();
      this.initSticky();
      this.initDelivery();
      this.initShare();
      this.initVideo();
      this.trackRecent();
    }

    /* Variants ---------------------------------------------------- */
    initOptions() {
      if (!this.data || !this.data.variants) return;
      this.optionInputs = qsa('[data-aj-option]', this.root);
      this.selects = qsa('select[data-aj-option-select]', this.root);
      this.root.addEventListener('change', (e) => {
        if (e.target.matches('[data-aj-option], select[data-aj-option-select]')) this.onOptionChange();
      });
      this.updateAvailability();
    }

    selectedOptions() {
      const groups = qsa('[data-aj-option-group]', this.root);
      return groups.map((group) => {
        const select = qs('select', group);
        if (select) return select.value;
        const checked = qs('input:checked', group);
        return checked ? checked.value : null;
      });
    }

    currentVariant() {
      const selected = this.selectedOptions();
      return this.data.variants.find((v) => v.options.every((opt, i) => opt === selected[i])) || null;
    }

    onOptionChange() {
      const variant = this.currentVariant();
      qsa('[data-aj-option-group]', this.root).forEach((group, i) => {
        const label = qs('[data-aj-option-value]', group);
        if (label) label.textContent = this.selectedOptions()[i] || '';
      });
      this.updateAvailability();
      this.updateButtons(variant);
      if (!variant) return;
      if (this.idInput) this.idInput.value = variant.id;
      qsa('[data-aj-variant-id]', this.root).forEach((el) => (el.value = variant.id));
      if (this.root.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      if (this.root.dataset.priceMode === 'json') this.renderPrice(variant);
      else this.refreshVariantSections(variant.id);
      if (variant.featured_media_id) this.goToMedia(variant.featured_media_id);
      this.root.dispatchEvent(new CustomEvent('aj:variant:change', { bubbles: true, detail: { variant } }));
    }

    updateButtons(variant) {
      const buttons = [this.atc, ...(this.sticky ? qsa('[data-aj-sticky-button]', this.sticky) : [])].filter(Boolean);
      buttons.forEach((btn) => {
        const label = qs('[data-aj-atc-label]', btn) || btn;
        if (!variant) {
          btn.disabled = true;
          label.textContent = strings.unavailable;
        } else if (!variant.available) {
          btn.disabled = true;
          label.textContent = strings.soldOut;
        } else {
          btn.disabled = false;
          label.textContent = variant.preorder && strings.preorder ? strings.preorder : strings.addToCart;
        }
      });
      const dynamic = qs('[data-aj-dynamic-checkout]', this.root);
      if (dynamic) dynamic.hidden = !variant || !variant.available;
    }

    updateAvailability() {
      const selected = this.selectedOptions();
      const groups = qsa('[data-aj-option-group]', this.root);
      groups.forEach((group, index) => {
        qsa('input[data-aj-option], option', group).forEach((input) => {
          const value = input.value;
          const possible = this.data.variants.some(
            (v) => v.available && v.options[index] === value && v.options.every((opt, i) => i === index || i > index || opt === selected[i])
          );
          const holder = input.closest('[data-aj-value]') || input;
          holder.classList.toggle('is-unavailable', !possible);
          if (input.tagName === 'OPTION') input.textContent = input.dataset.label + (possible ? '' : ` (${String(strings.soldOut || '').toLowerCase()})`);
        });
      });
    }

    // Lightweight price update (featured product sections outside the product template)
    renderPrice(variant) {
      const wrap = qs('[data-aj-price-wrap]', this.root);
      if (!wrap || !variant || typeof variant.price !== 'number') return;
      const onSale = variant.compare_at_price > variant.price;
      // A lower price (tagged product, see snippets/aj-price): the former price as "Avant …", no promotion look;
      // on large prices, the line saying it is not a promotion
      const newPrice = !!qs('.aj-price[data-newprice]', wrap);
      const size = wrap.dataset.size || 'lg';
      const hidden = (text) => (text ? `<span class="aj-visually-hidden">${escHTML(text)}</span>` : '');
      const state = onSale ? (newPrice ? ' aj-price--newprice' : ' aj-price--sale') : '';
      let html = `<div class="aj-price aj-price--${size}${state}${variant.available ? '' : ' aj-price--soldout'}"${newPrice ? ' data-newprice' : ''}>`;
      html += `<span class="aj-price__current">${onSale ? hidden(newPrice ? strings.newPrice : strings.salePrice) : ''}${moneyParts(variant.price)}</span>`;
      if (onSale && newPrice) {
        html += `<span class="aj-price__before">${escHTML(strings.before || '')} <s>${moneyParts(variant.compare_at_price)}</s></span>`;
        if (size === 'lg' && strings.newPriceNote) html += `<span class="aj-price__note">${escHTML(strings.newPriceNote)}</span>`;
      } else if (onSale) html += `<s class="aj-price__compare">${hidden(strings.regularPrice)}${moneyParts(variant.compare_at_price)}</s>`;
      html += '</div>';
      wrap.innerHTML = html;
    }

    async refreshVariantSections(variantId) {
      if (!this.sectionId || !this.url) return;
      const token = (this.refreshToken = (this.refreshToken || 0) + 1);
      try {
        const res = await fetch(`${this.url}?variant=${variantId}&section_id=${this.sectionId}`);
        const html = await res.text();
        if (token !== this.refreshToken) return;
        const doc = parseHTML(html);
        ['[data-aj-price-wrap]', '[data-aj-inventory]', '[data-aj-sticky-price]', '[data-aj-sku]'].forEach((sel) => {
          const fresh = qs(sel, doc);
          qsa(sel, this.root).forEach((el) => {
            if (fresh) el.innerHTML = fresh.innerHTML;
          });
        });
      } catch (e) {
        /* keep current markup */
      }
    }

    /* Gallery ----------------------------------------------------- */
    initGallery() {
      this.slides = [];
      this.thumbs = [];
      this.dots = [];
      this.gallery = qs('[data-aj-gallery]', this.root);
      if (!this.gallery) return;
      this.track = qs('[data-aj-gallery-track]', this.gallery);
      this.slides = qsa('[data-aj-slide]', this.gallery);
      this.thumbs = qsa('[data-aj-thumb]', this.gallery);
      this.dots = qsa('[data-aj-dot]', this.gallery);
      this.counter = qs('[data-aj-counter]', this.gallery);
      if (!this.track || !this.slides.length) return;

      this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.goTo(i)));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
      const prev = qs('[data-aj-gallery-prev]', this.gallery);
      const next = qs('[data-aj-gallery-next]', this.gallery);
      if (prev) prev.addEventListener('click', () => this.goTo(Math.max(0, this.index - 1)));
      if (next) next.addEventListener('click', () => this.goTo(Math.min(this.slides.length - 1, this.index + 1)));

      this.index = 0;
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio > 0.55) this.setActive(this.slides.indexOf(entry.target));
            });
          },
          { root: this.track, threshold: [0.6] }
        );
        this.slides.forEach((s) => io.observe(s));
      }

      // Hover zoom (desktop, fine pointer)
      if (settings.hoverZoom !== false && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        this.slides.forEach((slide) => {
          let zoomable = qs('[data-aj-zoomable]', slide);
          if (!zoomable && slide.classList.contains('aj-gallery__slide--image')) {
            zoomable = qs('img', slide);
            if (zoomable) zoomable.setAttribute('data-aj-zoomable', '');
          }
          if (!zoomable) return;
          slide.addEventListener('mousemove', (e) => {
            const r = slide.getBoundingClientRect();
            zoomable.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
          });
          slide.addEventListener('mouseenter', () => slide.classList.add('is-zooming'));
          slide.addEventListener('mouseleave', () => slide.classList.remove('is-zooming'));
        });
      }
      this.slides.forEach((slide, i) => {
        const opener = qs('[data-aj-lightbox-open]', slide);
        if (!opener) return;
        opener.addEventListener('click', () => {
          const n = parseInt(opener.dataset.ajLightboxOpen, 10);
          this.openLightbox(Number.isNaN(n) ? i : n);
        });
      });

      // Start on the selected variant's image (e.g. /products/x?variant=123)
      const selected = this.data && this.data.variants ? this.currentVariant() : null;
      if (selected && selected.featured_media_id) {
        const start = this.slides.findIndex((s) => s.dataset.mediaId === String(selected.featured_media_id));
        if (start > 0) requestAnimationFrame(() => this.goTo(start, true));
      }
    }

    setActive(i) {
      if (i < 0 || i === this.index) return;
      this.index = i;
      this.thumbs.forEach((t, n) => t.classList.toggle('is-active', n === i));
      this.thumbs.forEach((t, n) => t.setAttribute('aria-current', n === i ? 'true' : 'false'));
      this.dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
      if (this.counter) this.counter.textContent = `${i + 1} / ${this.slides.length}`;
    }

    goTo(i, instant = false) {
      const slide = this.slides[i];
      if (!slide || !this.track) return;
      if (this.track.scrollWidth > this.track.clientWidth + 2) {
        this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: instant || reducedMotion() ? 'auto' : 'smooth' });
      } else if (!instant && slide.getBoundingClientRect().top < 0) {
        // Stacked desktop gallery: bring the image into view instead
        slide.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
      }
      this.setActive(i);
    }

    goToMedia(mediaId) {
      if (!this.slides || !this.slides.length) return;
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i > -1) this.goTo(i);
    }

    /* Lightbox ---------------------------------------------------- */
    initLightbox() {
      this.lightbox = qs('[data-aj-lightbox]', this.root);
      if (!this.lightbox) return;
      this.lbTrack = qs('[data-aj-lightbox-track]', this.lightbox);
      this.lbSlides = qsa('[data-aj-lightbox-slide]', this.lightbox);
      this.lbCounter = qs('[data-aj-lightbox-counter]', this.lightbox);
      qsa('[data-aj-lightbox-close]', this.lightbox).forEach((b) => b.addEventListener('click', () => this.closeLightbox()));
      const prev = qs('[data-aj-lightbox-prev]', this.lightbox);
      const next = qs('[data-aj-lightbox-next]', this.lightbox);
      if (prev) prev.addEventListener('click', () => this.lightboxGo(this.lbIndex - 1));
      if (next) next.addEventListener('click', () => this.lightboxGo(this.lbIndex + 1));
      this.lightbox.addEventListener('click', (e) => {
        if (e.target === this.lightbox) this.closeLightbox();
      });
      this.lightbox.addEventListener('close', () => {
        if (!Layers.active) lockScroll(false);
      });
      this.lightbox.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.lightboxGo(this.lbIndex + 1);
        if (e.key === 'ArrowLeft') this.lightboxGo(this.lbIndex - 1);
      });
      this.lbSlides.forEach((slide) => {
        const img = qs('img', slide);
        if (!img) return;
        img.addEventListener('click', (e) => {
          const zoomed = slide.classList.toggle('is-zoomed');
          if (zoomed) {
            const r = img.getBoundingClientRect();
            img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
          }
        });
        img.addEventListener('mousemove', (e) => {
          if (!slide.classList.contains('is-zoomed')) return;
          const r = img.getBoundingClientRect();
          img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
        });
      });
      if (this.lbTrack && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.lbIndex = this.lbSlides.indexOf(entry.target);
                if (this.lbCounter) this.lbCounter.textContent = `${this.lbIndex + 1} / ${this.lbSlides.length}`;
              }
            });
          },
          { root: this.lbTrack, threshold: 0.6 }
        );
        this.lbSlides.forEach((s) => io.observe(s));
      }
    }

    openLightbox(i) {
      if (!this.lightbox || typeof this.lightbox.showModal !== 'function') return;
      this.lightbox.showModal();
      lockScroll(true);
      requestAnimationFrame(() => this.lightboxGo(i, true));
    }

    lightboxGo(i, instant = false) {
      if (!this.lbSlides || !this.lbSlides.length) return;
      const n = (i + this.lbSlides.length) % this.lbSlides.length;
      this.lbSlides.forEach((s) => s.classList.remove('is-zoomed'));
      this.lbTrack.scrollTo({ left: this.lbSlides[n].offsetLeft, behavior: instant || reducedMotion() ? 'auto' : 'smooth' });
      this.lbIndex = n;
      if (this.lbCounter) this.lbCounter.textContent = `${n + 1} / ${this.lbSlides.length}`;
    }

    closeLightbox() {
      if (this.lightbox && this.lightbox.open) this.lightbox.close();
      if (!Layers.active) lockScroll(false);
    }

    /* Sticky add to cart ------------------------------------------ */
    initSticky() {
      if (!this.sticky || !this.atc) return;
      const show = (past) => {
        if (past === this.sticky.classList.contains('is-visible')) return;
        this.sticky.classList.toggle('is-visible', past);
        this.sticky.setAttribute('aria-hidden', past ? 'false' : 'true');
      };
      if ('IntersectionObserver' in window) {
        // The bar is shown whenever the main add-to-cart button is off screen (not yet reached on phones, or
        // scrolled past), so buying is always one tap away. No scroll listener, no layout reads.
        new IntersectionObserver(([entry]) => show(!entry.isIntersecting)).observe(this.atc);
      } else {
        let ticking = false;
        const check = () => {
          ticking = false;
          const r = this.atc.getBoundingClientRect();
          show(r.bottom < 0 || r.top > window.innerHeight);
        };
        window.addEventListener(
          'scroll',
          () => {
            if (!ticking) {
              ticking = true;
              requestAnimationFrame(check);
            }
          },
          { passive: true }
        );
        window.addEventListener('resize', debounce(check, 150));
        requestAnimationFrame(check);
      }
      qsa('[data-aj-sticky-button]', this.sticky).forEach((btn) =>
        btn.addEventListener('click', () => {
          if (this.form && typeof this.form.requestSubmit === 'function') this.form.requestSubmit(this.atc);
          else if (this.atc) this.atc.click();
        })
      );
    }

    /* Delivery estimate -------------------------------------------- */
    initDelivery() {
      qsa('[data-aj-delivery]', this.root).forEach((el) => {
        const min = parseInt(el.dataset.min || '1', 10);
        const max = parseInt(el.dataset.max || '3', 10);
        const skipWeekend = el.dataset.skipWeekend === 'true';
        const addDays = (n) => {
          const d = new Date();
          let added = 0;
          while (added < n) {
            d.setDate(d.getDate() + 1);
            const day = d.getDay();
            if (skipWeekend && (day === 0 || day === 6)) continue;
            added += 1;
          }
          return d;
        };
        const lang = document.documentElement.lang || 'fr';
        let fmt;
        try {
          fmt = new Intl.DateTimeFormat(lang, { weekday: 'long', day: 'numeric', month: 'long' });
        } catch (e) {
          fmt = new Intl.DateTimeFormat('fr', { weekday: 'long', day: 'numeric', month: 'long' });
        }
        const start = qs('[data-aj-delivery-start]', el);
        const end = qs('[data-aj-delivery-end]', el);
        if (start) start.textContent = fmt.format(addDays(min));
        if (end) end.textContent = fmt.format(addDays(max));
        el.hidden = false;
      });
    }

    /* Share --------------------------------------------------------- */
    initShare() {
      qsa('[data-aj-copy-link]', this.root).forEach((btn) =>
        btn.addEventListener('click', async () => {
          const url = btn.dataset.ajCopyLink || window.location.href;
          try {
            if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
              await navigator.share({ url, title: document.title });
              return;
            }
            await navigator.clipboard.writeText(url);
            AJ.toast(strings.copied);
          } catch (e) {
            /* user cancelled */
          }
        })
      );
    }

    /* External video (metafield link) -------------------------------- */
    initVideo() {
      const btn = qs('[data-aj-video-open]', this.root);
      const dialog = qs('[data-aj-video-dialog]', this.root);
      if (!btn || !dialog || typeof dialog.showModal !== 'function') return;
      const frame = qs('[data-aj-video-frame]', dialog);
      btn.addEventListener('click', (e) => {
        const embed = toEmbed(btn.getAttribute('href'));
        if (!embed) return;
        e.preventDefault();
        // Built as an element: the product title is set as a property, never parsed as HTML
        const iframe = document.createElement('iframe');
        iframe.src = embed;
        iframe.title = btn.dataset.title || 'Video';
        iframe.allow = 'autoplay; fullscreen; picture-in-picture';
        iframe.allowFullscreen = true;
        frame.replaceChildren(iframe);
        dialog.showModal();
      });
      dialog.addEventListener('close', () => (frame.innerHTML = ''));
      qsa('[data-aj-video-close]', dialog).forEach((b) => b.addEventListener('click', () => dialog.close()));
      dialog.addEventListener('click', (e) => e.target === dialog && dialog.close());
    }

    /* Recently viewed ------------------------------------------------ */
    trackRecent() {
      const handle = this.root.dataset.handle;
      if (!handle) return;
      try {
        const list = JSON.parse(localStorage.getItem('aj_recent') || '[]').filter((h) => h !== handle);
        list.unshift(handle);
        localStorage.setItem('aj_recent', JSON.stringify(list.slice(0, 12)));
      } catch (e) {
        /* storage unavailable */
      }
    }
  }
  AJ.Product = Product;

  function toEmbed(url) {
    if (!url) return null;
    const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
    if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&rel=0`;
    const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`;
    return null;
  }

  function initProducts(root = document) {
    qsa('[data-aj-product]', root).forEach((el) => {
      if (el.dataset.ajInit) return;
      el.dataset.ajInit = '1';
      el.ajProduct = new Product(el);
    });
  }

  /* ------------------------------------------------------------------ *
   * Quick view: the reading glass of a product card shows the product in a window over the page
   * (pictures, options, add to cart), fetched with the Section Rendering API. The picture and the
   * title of the card keep opening the product page.
   * ------------------------------------------------------------------ */
  const QuickView = {
    el: null,
    seq: 0,
    cache: new Map(),
    urlFor(href) {
      const url = new URL(href, window.location.origin);
      url.searchParams.set('section_id', 'aj-quick-view');
      return url.pathname + url.search;
    },
    // Started on hover or focus of the button, so the window usually fills at once
    load(href) {
      const url = this.urlFor(href);
      if (!this.cache.has(url)) {
        const request = fetch(url, { credentials: 'same-origin' })
          .then((res) => {
            if (!res.ok) throw new Error(String(res.status));
            return res.text();
          })
          .catch((err) => {
            this.cache.delete(url);
            throw err;
          });
        this.cache.set(url, request);
        if (this.cache.size > 16) this.cache.delete(this.cache.keys().next().value);
      }
      return this.cache.get(url);
    },
    // The product stylesheet (options, quantity, button) is only on product pages: load it before showing
    styles(doc) {
      const have = (path) => qsa('link[rel="stylesheet"]').some((l) => (l.getAttribute('href') || '').split('?')[0] === path);
      const waits = qsa('link[rel="stylesheet"]', doc)
        .map((link) => {
          const href = link.getAttribute('href');
          if (!href || have(href.split('?')[0])) return null;
          const fresh = document.createElement('link');
          fresh.rel = 'stylesheet';
          fresh.href = href;
          const ready = new Promise((resolve) => {
            fresh.onload = resolve;
            fresh.onerror = resolve;
            setTimeout(resolve, 2500);
          });
          document.head.appendChild(fresh);
          return ready;
        })
        .filter(Boolean);
      return Promise.all(waits);
    },
    build() {
      const el = document.createElement('div');
      el.className = 'aj-qv';
      el.id = 'AjQuickView';
      el.setAttribute('data-aj-quickview', '');
      el.setAttribute('aria-hidden', 'true');
      el.innerHTML =
        '<div class="aj-qv__overlay" data-aj-close></div>' +
        '<div class="aj-qv__panel" role="dialog" aria-modal="true" tabindex="-1">' +
        '<button type="button" class="aj-qv__close" data-aj-close><svg class="aj-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
        '<div class="aj-qv__body" data-aj-qv-body></div>' +
        '</div>';
      qs('.aj-qv__close', el).setAttribute('aria-label', strings.close || 'Close');
      // the "full details" link follows the chosen option
      el.addEventListener('aj:variant:change', (e) => {
        const more = qs('[data-aj-qv-more]', el);
        const variant = e.detail && e.detail.variant;
        if (!more || !variant) return;
        const url = new URL(more.href, window.location.origin);
        url.searchParams.set('variant', variant.id);
        more.href = url.pathname + url.search;
      });
      // emptied once closed: no hidden forms or duplicate ids left in the page
      el.addEventListener('aj:layer:close', () => {
        this.seq += 1;
        setTimeout(() => {
          if (!el.classList.contains('is-open')) qs('[data-aj-qv-body]', el).innerHTML = '';
        }, 450);
      });
      document.body.appendChild(el);
      this.el = el;
      return el;
    },
    skeleton() {
      return (
        '<div class="aj-qv__product aj-qv__product--loading" aria-hidden="true">' +
        '<div class="aj-qv__gallery"><span class="aj-qv__frame aj-qv__ghost"></span></div>' +
        '<div class="aj-qv__info"><span class="aj-qv__ghost aj-qv__ghost--line"></span><span class="aj-qv__ghost aj-qv__ghost--title"></span>' +
        '<span class="aj-qv__ghost aj-qv__ghost--line"></span><span class="aj-qv__ghost aj-qv__ghost--text"></span><span class="aj-qv__ghost aj-qv__ghost--button"></span></div>' +
        '</div>'
      );
    },
    async open(href, opener) {
      track('ajbir_quick_view_opened', { url: href });
      const el = this.el || this.build();
      const panel = qs('.aj-qv__panel', el);
      const body = qs('[data-aj-qv-body]', el);
      body.innerHTML = this.skeleton();
      panel.removeAttribute('aria-labelledby');
      panel.setAttribute('aria-label', strings.quickView || '');
      panel.setAttribute('aria-busy', 'true');
      el.classList.add('is-loading');
      Layers.open(el, opener);
      const seq = (this.seq += 1);
      try {
        const html = await this.load(href);
        const doc = parseHTML(html);
        const content = qs('[data-aj-qv-content]', doc);
        if (!content) throw new Error('empty');
        await this.styles(doc);
        if (seq !== this.seq) return;
        body.innerHTML = '';
        body.appendChild(document.importNode(content, true));
        initProducts(body);
        Wishlist.sync(body);
        const title = content.dataset.titleId && document.getElementById(content.dataset.titleId);
        if (title) {
          panel.removeAttribute('aria-label');
          panel.setAttribute('aria-labelledby', title.id);
        }
      } catch (err) {
        if (seq !== this.seq) return;
        body.innerHTML = '<div class="aj-qv__error"><p></p><a class="aj-btn aj-btn--primary"></a></div>';
        qs('.aj-qv__error p', body).textContent = strings.quickViewError || '';
        const link = qs('.aj-qv__error a', body);
        link.href = href;
        link.textContent = strings.viewDetails || href;
      } finally {
        if (seq === this.seq) {
          el.classList.remove('is-loading');
          panel.removeAttribute('aria-busy');
        }
      }
    },
  };
  AJ.QuickView = QuickView;

  document.addEventListener('click', (event) => {
    const btn = event.target.closest && event.target.closest('[data-aj-quick-view]');
    if (!btn) return;
    event.preventDefault();
    QuickView.open(btn.getAttribute('data-aj-quick-view'), btn);
  });
  ['mouseover', 'focusin', 'touchstart'].forEach((type) =>
    document.addEventListener(
      type,
      (event) => {
        const btn = event.target.closest && event.target.closest('[data-aj-quick-view]');
        if (btn) QuickView.load(btn.getAttribute('data-aj-quick-view')).catch(() => {});
      },
      { passive: true }
    )
  );

  /* ------------------------------------------------------------------ *
   * Recommendations & recently viewed (lazy)
   * ------------------------------------------------------------------ */
  function initRecommendations(root = document) {
    qsa('[data-aj-recommendations]', root).forEach(async (el) => {
      if (el.dataset.ajInit) return;
      el.dataset.ajInit = '1';
      const url = el.dataset.url;
      if (!url) return;
      try {
        const res = await fetch(url);
        const doc = parseHTML(await res.text());
        const fresh = qs('[data-aj-recommendations]', doc);
        const wrap = el.closest('[data-aj-recommendations-wrap]');
        if (fresh && fresh.innerHTML.trim()) {
          el.innerHTML = fresh.innerHTML;
          el.classList.add('is-loaded');
          if (wrap) wrap.hidden = false;
          initSliders(el);
          initReveal(el);
          Wishlist.sync(el);
        } else if (!(window.Shopify && window.Shopify.designMode)) {
          const target = wrap || el.closest('.shopify-section');
          if (target) target.hidden = true;
        }
      } catch (e) {
        /* leave placeholder */
      }
    });
  }

  function initRecentlyViewed(root = document) {
    qsa('[data-aj-recent]', root).forEach(async (el) => {
      if (el.dataset.ajInit) return;
      el.dataset.ajInit = '1';
      const current = el.dataset.current;
      const limit = parseInt(el.dataset.limit || '4', 10);
      let handles = [];
      try {
        handles = JSON.parse(localStorage.getItem('aj_recent') || '[]').filter((h) => h && h !== current).slice(0, limit);
      } catch (e) {
        handles = [];
      }
      const section = el.closest('.shopify-section');
      if (!handles.length) {
        if (section && !window.Shopify?.designMode) section.hidden = true;
        return;
      }
      const list = qs('[data-aj-recent-list]', el);
      const cards = await Promise.all(
        handles.map((h) =>
          fetch(`${routes.root.replace(/\/$/, '')}/products/${encodeURIComponent(h)}?section_id=aj-card`)
            .then((r) => (r.ok ? r.text() : ''))
            .then((html) => (html ? sectionHTML(html, '[data-aj-card-root]') : null))
            .catch(() => null)
        )
      );
      list.innerHTML = cards.filter((html) => html && html.trim()).map((html) => `<li>${html}</li>`).join('');
      if (!list.children.length) {
        if (section && !window.Shopify?.designMode) section.hidden = true;
        return;
      }
      el.hidden = false;
      initSliders(el);
      qsa('[data-aj-slider]', el).forEach((slider) => slider.ajUpdate && slider.ajUpdate());
      initReveal(el);
      Wishlist.sync(el);
    });
  }

  /* ------------------------------------------------------------------ *
   * Collection / search filters (Section Rendering API)
   * ------------------------------------------------------------------ */
  class Facets {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.section;
      this.form = qs('form[data-aj-facets-form]', root);
      this.results = qs('[data-aj-results]', root);
      if (!this.form) return;
      this.onChange = debounce(() => this.submit(), 450);
      // Listen on the section: the sort select sits outside the form (form="…" attribute)
      root.addEventListener('change', (e) => {
        if (e.target.form === this.form && e.target.matches('input, select')) this.onChange();
      });
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.submit();
      });
      root.addEventListener('click', (e) => {
        const link = e.target.closest('a[data-aj-facet-link]');
        if (link) {
          e.preventDefault();
          this.fetch(new URL(link.href, window.location.origin).searchParams.toString());
          return;
        }
        const more = e.target.closest('[data-aj-load-more]');
        if (more) {
          e.preventDefault();
          this.loadMore(more);
          return;
        }
        const view = e.target.closest('[data-aj-grid-view]');
        if (view) this.setView(view.dataset.ajGridView);
      });
      this.restoreView();
      this.initSidebar();
      window.addEventListener('popstate', () => this.fetch(window.location.search.slice(1), false));
    }

    initSidebar() {
      const drawer = qs('.aj-coll-body--sidebar [data-aj-drawer="filters"]', this.root);
      if (!drawer || !window.matchMedia) return;
      const panel = qs('.aj-drawer__panel', drawer);
      const mq = window.matchMedia('(min-width: 990px)');
      const apply = () => {
        if (mq.matches) {
          if (drawer.classList.contains('is-open')) Layers.close(drawer, false);
          drawer.removeAttribute('aria-hidden');
          if (panel) {
            panel.removeAttribute('role');
            panel.removeAttribute('aria-modal');
          }
        } else {
          if (!drawer.classList.contains('is-open')) drawer.setAttribute('aria-hidden', 'true');
          if (panel) {
            panel.setAttribute('role', 'dialog');
            panel.setAttribute('aria-modal', 'true');
          }
        }
      };
      apply();
      if (mq.addEventListener) mq.addEventListener('change', apply);
      else if (mq.addListener) mq.addListener(apply);
    }

    params() {
      const fd = new FormData(this.form);
      const params = new URLSearchParams();
      for (const [key, value] of fd.entries()) {
        if (value === '' || value === null) continue;
        params.append(key, value);
      }
      return params.toString();
    }

    submit() {
      this.fetch(this.params());
    }

    async fetch(query, push = true) {
      if (this.controller) this.controller.abort();
      const controller = (this.controller = window.AbortController ? new AbortController() : null);
      this.results = qs('[data-aj-results]', this.root);
      if (this.results) this.results.classList.add('is-loading');
      const base = window.location.pathname;
      const url = `${base}?${query}${query ? '&' : ''}section_id=${this.sectionId}`;
      try {
        const res = await fetch(url, controller ? { signal: controller.signal } : undefined);
        const doc = parseHTML(await res.text());
        if (controller && controller !== this.controller) return;
        // What the shopper is doing in the panel survives the refresh
        const active = document.activeElement;
        const focusId = active && active.id && this.root.contains(active) ? active.id : null;
        const typed = focusId && active.matches('[data-aj-price-input]') ? active.value : null;
        const groups = {};
        qsa('details[data-aj-filter-key]', this.root).forEach((d) => (groups[d.dataset.ajFilterKey] = d.open));
        ['[data-aj-results-wrap]', '[data-aj-facets-body]', '[data-aj-active-filters]', '[data-aj-count]', '[data-aj-count-button]', '[data-aj-filter-count]'].forEach((sel) => {
          const fresh = qs(sel, doc);
          qsa(sel, this.root).forEach((el) => {
            el.innerHTML = fresh ? fresh.innerHTML : '';
          });
        });
        qsa('details[data-aj-filter-key]', this.root).forEach((d) => {
          if (d.dataset.ajFilterKey in groups) d.open = groups[d.dataset.ajFilterKey];
        });
        if (focusId) {
          const again = document.getElementById(focusId);
          if (again && again !== document.activeElement) {
            if (typed !== null) again.value = typed;
            again.focus({ preventScroll: true });
          }
        }
        const status = qs('[data-aj-facets-status]', this.root);
        const count = qs('[data-aj-count]', this.root);
        if (status && count) status.textContent = count.textContent.trim();
        if (push) window.history.replaceState({}, '', `${base}${query ? '?' + query : ''}`);
        this.results = qs('[data-aj-results]', this.root);
        initReveal(this.root);
        Wishlist.sync(this.root);
        this.restoreView();
      } catch (e) {
        if (e && e.name === 'AbortError') return;
        window.location.href = `${base}?${query}`;
      } finally {
        if (!controller || controller === this.controller) {
          const results = qs('[data-aj-results]', this.root);
          if (results) results.classList.remove('is-loading');
        }
      }
    }

    async loadMore(button) {
      const href = button.getAttribute('href');
      if (!href) return;
      button.classList.add('is-loading');
      try {
        const url = new URL(href, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);
        const doc = parseHTML(await (await fetch(url.toString())).text());
        const freshResults = qs('[data-aj-results]', doc);
        this.results = qs('[data-aj-results]', this.root);
        if (freshResults && this.results) {
          Array.from(freshResults.children).forEach((child) => this.results.appendChild(child));
          // sold-out products last: the sold-out cards already loaded move after the new available ones
          if (settings.soldoutLast) qsa(':scope > [data-aj-soldout]', this.results).forEach((li) => this.results.appendChild(li));
        }
        const freshPagination = qs('[data-aj-pagination]', doc);
        const pagination = qs('[data-aj-pagination]', this.root);
        if (pagination) pagination.innerHTML = freshPagination ? freshPagination.innerHTML : '';
        initReveal(this.root);
        Wishlist.sync(this.root);
      } catch (e) {
        window.location.href = href;
      }
    }

    setView(cols) {
      const results = qs('[data-aj-results]', this.root);
      if (!results) return;
      results.style.setProperty('--cols', cols);
      qsa('[data-aj-grid-view]', this.root).forEach((b) => b.setAttribute('aria-pressed', b.dataset.ajGridView === cols ? 'true' : 'false'));
      try {
        localStorage.setItem('aj_grid_view', cols);
      } catch (e) {
        /* ignore */
      }
    }

    restoreView() {
      let cols = null;
      try {
        cols = localStorage.getItem('aj_grid_view');
      } catch (e) {
        cols = null;
      }
      if (cols && qs(`[data-aj-grid-view="${cols}"]`, this.root)) this.setView(cols);
    }
  }
  AJ.Facets = Facets;

  function initFacets(root = document) {
    qsa('[data-aj-facets]', root).forEach((el) => {
      if (el.dataset.ajInit) return;
      el.dataset.ajInit = '1';
      el.ajFacets = new Facets(el);
    });
  }

  /* Description "read more" (collection hero) ------------------------- */
  function initReadMore(root = document) {
    qsa('[data-aj-readmore]', root).forEach((wrap) => {
      if (wrap.dataset.ajInit) return;
      wrap.dataset.ajInit = '1';
      const content = qs('[data-aj-readmore-content]', wrap);
      const btn = qs('[data-aj-readmore-toggle]', wrap);
      if (!content || !btn) return;
      btn.addEventListener('click', () => {
        const expanded = content.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        btn.textContent = expanded ? btn.dataset.less : btn.dataset.more;
      });
      // Measure on the next frame rather than forcing a layout while the page boots.
      requestAnimationFrame(() => {
        if (content.scrollHeight <= content.clientHeight + 4) {
          btn.hidden = true;
          content.classList.add('is-expanded');
        } else {
          btn.hidden = false;
        }
      });
    });
  }

  /* Business page contact bar (phones): shown once the page's own buttons have scrolled away ---------- */
  function initCtaBar(root = document) {
    qsa('[data-aj-cta-bar]', root).forEach((bar) => {
      if (bar.dataset.ajCtaReady) return;
      bar.dataset.ajCtaReady = '1';
      if (!('IntersectionObserver' in window)) return;
      // Zones where the bar steps aside: the hero buttons, the quote form, the closing call to action, the footer.
      const zones = qsa('[data-aj-cta-zone], .aj-footer');
      const seen = new Set();
      let started = false;
      const update = () => {
        const show = started && seen.size === 0;
        bar.classList.toggle('is-shown', show);
        document.body.classList.toggle('aj-has-ctabar', show);
      };
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) seen.add(entry.target);
          else seen.delete(entry.target);
        });
        started = true;
        update();
      }, { rootMargin: '0px 0px -24px 0px' });
      zones.forEach((zone) => io.observe(zone));
      if (!zones.length) {
        started = true;
        update();
      }
    });
  }

  /* Forms sent by email: unanswered optional questions are left out of the message ---------- */
  function initSkipEmpty(root = document) {
    qsa('form[data-aj-skip-empty]', root).forEach((form) => {
      if (form.dataset.ajSkipReady) return;
      form.dataset.ajSkipReady = '1';
      form.addEventListener('submit', () => {
        const skipped = [];
        qsa('input, select, textarea', form).forEach((field) => {
          if (!field.name || field.type === 'hidden' || field.type === 'checkbox' || field.type === 'radio' || field.required || field.disabled) return;
          if (String(field.value).trim() === '') {
            skipped.push([field, field.name]);
            field.removeAttribute('name');
          }
        });
        // the form data is gathered right after this handler: put the names back for a later visit (back button)
        window.setTimeout(() => skipped.forEach(([field, name]) => field.setAttribute('name', name)), 0);
      });
    });
  }

  /* Client references: on phones, show every logo after the first rows ---------- */
  /* FAQ page: search through the questions, and the theme being read is marked in the list of themes */
  function initFaq(root = document) {
    qsa('[data-aj-faq]', root).forEach((faq) => {
      if (faq.dataset.ajInit) return;
      faq.dataset.ajInit = '1';
      const items = qsa('[data-aj-faq-item]', faq);
      const groups = qsa('[data-aj-faq-group]', faq);
      const links = qsa('[data-aj-faq-link]', faq);
      const fold = (text) => (text || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[’']/g, ' ');
      const input = qs('[data-aj-faq-search]', faq);
      if (input) {
        qs('[data-aj-faq-search-wrap]', faq).hidden = false;
        const empty = qs('[data-aj-faq-empty]', faq);
        const live = qs('[data-aj-faq-live]', faq);
        const index = items.map((item) => fold(item.textContent));
        const wasOpen = items.map((item) => item.open);
        const run = () => {
          const words = fold(input.value).split(/\s+/).filter((w) => w.length > 1);
          let shown = 0;
          items.forEach((item, i) => {
            const match = !words.length || words.every((w) => index[i].includes(w));
            item.hidden = !match;
            if (match) shown += 1;
          });
          // a handful of answers: open them; back to how they were when the search is cleared
          items.forEach((item, i) => {
            if (!words.length) item.open = wasOpen[i];
            else if (!item.hidden && shown <= 3) item.open = true;
          });
          groups.forEach((group) => {
            group.hidden = words.length > 0 && !qsa('[data-aj-faq-item]', group).some((item) => !item.hidden);
          });
          links.forEach((link) => {
            const target = document.getElementById((link.getAttribute('href') || '').slice(1));
            link.classList.toggle('is-dim', Boolean(target && target.hidden));
          });
          if (empty) empty.hidden = !words.length || shown > 0;
          if (live) live.textContent = words.length ? (shown === 1 ? strings.faqFoundOne || '' : (strings.faqFoundOther || '').replace('[count]', shown)) : '';
        };
        input.addEventListener('input', debounce(run, 120));
        input.addEventListener('search', run);
      }
      // the theme being read is marked in the list
      if (links.length && 'IntersectionObserver' in window) {
        const byId = new Map(links.map((link) => [(link.getAttribute('href') || '').slice(1), link]));
        const visible = new Set();
        const mark = () => {
          const first = groups.find((g) => visible.has(g.id));
          links.forEach((link) => {
            const on = Boolean(first) && byId.get(first.id) === link;
            link.classList.toggle('is-active', on);
            if (on) link.setAttribute('aria-current', 'true');
            else link.removeAttribute('aria-current');
          });
        };
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => (entry.isIntersecting ? visible.add(entry.target.id) : visible.delete(entry.target.id)));
            mark();
          },
          { rootMargin: '-35% 0px -55% 0px' }
        );
        groups.forEach((g) => g.id && io.observe(g));
      }
    });
  }

  function initRefs(root = document) {
    qsa('[data-aj-refs-more]', root).forEach((btn) => {
      if (btn.dataset.ajRefsReady) return;
      btn.dataset.ajRefsReady = '1';
      const section = btn.closest('[data-aj-refs]');
      if (!section) return;
      const more = btn.textContent;
      btn.addEventListener('click', () => {
        const open = !section.classList.contains('is-expanded');
        section.classList.toggle('is-expanded', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.textContent = open ? btn.dataset.labelLess || more : more;
        if (!open) section.scrollIntoView({ block: 'start', behavior: 'auto' });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Store policies: chapter labels, table of contents, print
   * ------------------------------------------------------------------ */
  function initPolicy(root = document) {
    qsa('[data-aj-policy-body]', root).forEach((body) => {
      if (body.dataset.ajPolicyReady) return;
      body.dataset.ajPolicyReady = '1';
      const norm = (str) => str.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

      // A heading shown on its own line doesn't need the colon typed after it ("Zone de livraison :").
      const trimColon = (el) => {
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
        let last = null;
        while (walker.nextNode()) if (walker.currentNode.textContent.trim()) last = walker.currentNode;
        if (last) last.textContent = last.textContent.replace(/[\s\u00a0]*:[\s\u00a0]*$/, '');
      };
      qsa('h2, h3, h4', body).forEach(trimColon);

      // Paragraphs that act as headings in policies typed by hand:
      // "1. Zone de livraison :" in bold, "2.4 Paiement" alone on its line, "COORDONNÉES" in capitals.
      qsa(':scope > p', body).forEach((p) => {
        const text = p.textContent.trim();
        if (!text) return;
        const first = Array.from(p.childNodes).find((n) => n.nodeType !== 3 || n.textContent.trim());
        if (first && first.nodeType === 1 && /^(STRONG|B)$/.test(first.tagName)) {
          const label = first.textContent.trim();
          const rest = text.slice(label.length).trim();
          if (label.length < 90 && (!rest || /^\d+(?:[.\-]\d+)*[.)]?\s/.test(label))) {
            p.classList.add('aj-policy__label');
            trimColon(first);
            // the sentence after the label now starts its own line: give it a capital letter
            let next = first.nextSibling;
            while (next && next.nodeType === 3 && !next.textContent.trim()) next = next.nextSibling;
            if (next && next.nodeType === 3) next.textContent = next.textContent.replace(/^(\s*)(\p{Ll})/u, (m, sp, c) => sp + c.toUpperCase());
          }
        } else if (text.length < 70 && /^\d+(?:[.\-]\d+)+\.?\s+\S/.test(text) && !/[.;:,]$/.test(text)) {
          p.classList.add('aj-policy__sub');
        } else if (text.length < 60 && text === text.toUpperCase() && /[A-ZÀ-Ý]{3}/.test(text)) {
          p.classList.add('aj-policy__caps');
        }
      });

      const policy = body.closest('.aj-policy');
      const toc = policy && qs('[data-aj-toc]', policy);
      const level = body.dataset.ajTocLevel;
      if (!toc || !level) return;
      let heads = qsa(level, body).filter((h) => h.textContent.trim());
      // A first heading that repeats the page title is the document's own title, not a chapter.
      const firstHead = heads[0];
      if (firstHead) {
        const range = document.createRange();
        range.setStart(body, 0);
        range.setEndBefore(firstHead);
        const words = norm(body.dataset.ajTitle || '').split(/[^a-z]+/).filter((w) => w.length >= 5);
        if (!range.toString().trim() && words.some((w) => norm(firstHead.textContent).includes(w))) {
          firstHead.classList.add('aj-policy__doc-title');
          heads = heads.slice(1);
        }
      }
      if (heads.length < 2) return;

      const list = qs('[data-aj-toc-list]', toc);
      const used = new Set();
      const links = heads.map((h, i) => {
        if (!h.id) {
          const base = 'chapitre-' + (norm(h.textContent).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48) || String(i + 1));
          let id = base;
          let n = 2;
          while (used.has(id) || document.getElementById(id)) id = `${base}-${n++}`;
          h.id = id;
        }
        used.add(h.id);
        const full = h.textContent.replace(/\s+/g, ' ').trim();
        const m = full.match(/^(?:article|art\.)?\s*(\d+(?:[.\-]\d+)*)\s*[-–—:.)]?\s+(.+)$/i);
        const li = document.createElement('li');
        const a = document.createElement('a');
        const num = document.createElement('span');
        const label = document.createElement('span');
        a.className = 'aj-toc__link';
        a.href = `#${h.id}`;
        num.className = 'aj-toc__num';
        label.className = 'aj-toc__label';
        if (m) {
          num.textContent = m[1];
          label.textContent = m[2].replace(/\s*:$/, '');
        } else {
          num.classList.add('aj-toc__num--mark');
          label.textContent = full.replace(/\s*:$/, '');
        }
        a.append(num, label);
        li.append(a);
        list.append(li);
        return a;
      });
      const count = qs('[data-aj-toc-count]', toc);
      if (count) count.textContent = String(links.length);
      const wide = window.matchMedia('(min-width: 990px)');
      if (wide.matches) toc.open = true;
      list.addEventListener('click', (e) => {
        if (e.target.closest('a') && !wide.matches) toc.open = false;
      });

      // Highlight the chapter being read
      if ('IntersectionObserver' in window) {
        const byId = new Map(links.map((a) => [a.getAttribute('href').slice(1), a]));
        let current = null;
        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const a = byId.get(entry.target.id);
            if (!a || a === current) return;
            if (current) {
              current.classList.remove('is-active');
              current.removeAttribute('aria-current');
            }
            a.classList.add('is-active');
            a.setAttribute('aria-current', 'location');
            current = a;
          });
        }, { rootMargin: '-12% 0px -70% 0px' });
        heads.forEach((h) => io.observe(h));
      }
    });

    qsa('[data-aj-print]', root).forEach((btn) => {
      if (btn.dataset.ajBound) return;
      btn.dataset.ajBound = '1';
      btn.addEventListener('click', () => window.print());
    });
  }

  /* ------------------------------------------------------------------ *
   * Boot + theme editor support
   * ------------------------------------------------------------------ */
  // One failing module must never stop the others (content stays visible, cart keeps working)
  const safely = (fn, ...args) => {
    try {
      fn(...args);
    } catch (err) {
      if (window.console) console.error('[AJBIR]', err);
    }
  };

  function initAll(root = document) {
    [
      initAnnouncement,
      initMegaMenus,
      initSliders,
      initSlideshows,
      initVideos,
      initFooter,
      initProducts,
      initFacets,
      initReadMore,
      initPolicy,
      initCtaBar,
      initRefs,
      initFaq,
      initSkipEmpty,
      initRecommendations,
      initRecentlyViewed,
      initWishlistPage,
      initReveal,
      initParallax,
    ].forEach((fn) => safely(fn, root));
    safely(() => Wishlist.sync(root));
  }

  // "Copy link" buttons outside product sections (journal articles…); product buttons are wired by Product.
  function initCopyLinks() {
    if (initCopyLinks.bound) return;
    initCopyLinks.bound = true;
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest && e.target.closest('[data-aj-copy-link]');
      if (!btn || btn.closest('[data-aj-product]')) return;
      const url = btn.dataset.ajCopyLink || window.location.href;
      try {
        if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
          await navigator.share({ url, title: document.title });
          return;
        }
        await navigator.clipboard.writeText(url);
        AJ.toast(strings.copied);
      } catch (err) {
        /* user cancelled or clipboard unavailable */
      }
    });
  }

  function boot() {
    if (settings.hoverZoom !== false) document.body.classList.add('aj-hover-zoom');
    safely(initCopyLinks);
    safely(initStickyHeader);
    safely(initSearch);
    initAll(document);
    safely(bindLegacyCart);
    window.addEventListener('load', bindLegacyCart, { once: true });
    AJ.booted = true;
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => {
    initAll(e.target);
    if (e.target.querySelector('[data-aj-header-group]')) {
      initStickyHeader();
      initSearch();
    }
  });
  document.addEventListener('shopify:section:select', (e) => {
    const drawer = qs('[data-aj-cart-drawer]', e.target);
    if (drawer) Layers.open(drawer);
    const bar = qs('[data-aj-cta-bar]', e.target);
    if (bar) bar.classList.add('is-shown');
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const drawer = qs('[data-aj-cart-drawer]', e.target);
    if (drawer) Layers.close(drawer, false);
    const bar = qs('[data-aj-cta-bar]', e.target);
    if (bar) bar.classList.remove('is-shown');
  });
  document.addEventListener('shopify:block:select', (e) => {
    const item = e.target.closest('[data-aj-mega-item]');
    if (item) item.classList.add('is-open');
    const slide = e.target.closest('[data-aj-slideshow-slide]');
    if (slide && slide.parentElement) slide.parentElement.scrollTo({ left: slide.offsetLeft - slide.parentElement.offsetLeft, behavior: 'auto' });
    const message = e.target.closest('[data-aj-announcement-message]');
    if (message) {
      qsa('[data-aj-announcement-message]', message.parentElement).forEach((m) => m.classList.toggle('is-active', m === message));
    }
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const item = e.target.closest('[data-aj-mega-item]');
    if (item) item.classList.remove('is-open');
  });
})();
